import { useState, useEffect, useCallback } from 'react';

// Store deferred prompt globally in module scope so it survives component unmounts
let globalDeferredPrompt: any = null;
const listeners = new Set<() => void>();

function notifyListeners() {
  listeners.forEach((fn) => fn());
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e: Event) => {
    e.preventDefault();
    globalDeferredPrompt = e;
    notifyListeners();
  });

  window.addEventListener('appinstalled', () => {
    globalDeferredPrompt = null;
    notifyListeners();
  });
}

// App-installation via PWA er aktiv og tilgængelig on-demand for brugere via menu og konto.
export const PWA_INSTALL_SUSPENDED = false;

export function isRunningStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const isStandaloneMedia =
    window.matchMedia?.('(display-mode: standalone)')?.matches ||
    window.matchMedia?.('(display-mode: fullscreen)')?.matches ||
    window.matchMedia?.('(display-mode: minimal-ui)')?.matches ||
    false;
  const isIosStandalone =
    (window.navigator as unknown as { standalone?: boolean })?.standalone === true;
  const isAndroidWebApk =
    typeof document !== 'undefined' &&
    typeof document.referrer === 'string' &&
    document.referrer.startsWith('android-app://');
  return Boolean(isStandaloneMedia || isIosStandalone || isAndroidWebApk);
}

export function isIosDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /iphone|ipad|ipod/i.test(ua);
}

export interface PwaInstallState {
  isInstalled: boolean;
  isInstallable: boolean;
  isIos: boolean;
  canPromptDirectly: boolean;
  isSuspended: boolean;
  promptInstall: () => Promise<'accepted' | 'dismissed' | 'manual_ios' | 'unsupported'>;
}

export function usePwaInstall(): PwaInstallState {
  const [hasPrompt, setHasPrompt] = useState<boolean>(() => Boolean(globalDeferredPrompt));
  const [installed, setInstalled] = useState<boolean>(() => isRunningStandalone());
  const [isIos] = useState<boolean>(() => isIosDevice());

  useEffect(() => {
    const handleUpdate = () => {
      setHasPrompt(Boolean(globalDeferredPrompt));
      setInstalled(isRunningStandalone());
    };

    listeners.add(handleUpdate);
    return () => {
      listeners.delete(handleUpdate);
    };
  }, []);

  const promptInstall = useCallback(async (): Promise<
    'accepted' | 'dismissed' | 'manual_ios' | 'unsupported'
  > => {
    if (PWA_INSTALL_SUSPENDED) {
      return 'unsupported';
    }

    if (globalDeferredPrompt) {
      try {
        await globalDeferredPrompt.prompt();
        const choice = await globalDeferredPrompt.userChoice;
        globalDeferredPrompt = null;
        setHasPrompt(false);
        notifyListeners();
        return choice.outcome === 'accepted' ? 'accepted' : 'dismissed';
      } catch {
        return 'unsupported';
      }
    }

    if (isIos && !installed) {
      return 'manual_ios';
    }

    return 'unsupported';
  }, [isIos, installed]);

  // Når installation er suspenderet, må appen aldrig markeres som installerbar for almindelige brugere
  const isInstallable = !PWA_INSTALL_SUSPENDED && !installed;
  const canPromptDirectly = !PWA_INSTALL_SUSPENDED && hasPrompt;

  return {
    isInstalled: installed,
    isInstallable,
    isIos,
    canPromptDirectly,
    isSuspended: PWA_INSTALL_SUSPENDED,
    promptInstall,
  };
}
